// «Quando succede» con un'API (tela, artboard 49): la richiesta (percorso e parametri), il campo
// del JSON da guardare, il confronto e quando parte il giro. PopTown controlla da solo a ogni
// cadenza, senza sessioni finché la condizione non regge.
import type { ReactNode } from 'react';
import { Field, TextArea, TextField } from '../../components/Field';
import { Select } from '../../components/Select';
import type { RoutineCondition } from '../../lib/api';
import { Choice } from './Choice';

const OPS: { value: RoutineCondition['op']; label: string }[] = [
  { value: '>', label: '>' },
  { value: '>=', label: '≥' },
  { value: '=', label: '=' },
  { value: '!=', label: '≠' },
  { value: '<', label: '<' },
  { value: '<=', label: '≤' },
  { value: 'contains', label: 'contiene' },
];

/** I parametri come righe `nome=valore`, e ritorno. */
export function paramsText(p: Record<string, string> | undefined): string {
  return Object.entries(p ?? {})
    .map(([k, v]) => `${k}=${v}`)
    .join('\n');
}
export function parseParams(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const at = line.indexOf('=');
    if (at > 0) out[line.slice(0, at).trim()] = line.slice(at + 1).trim();
  }
  return out;
}

export function ApiFields({
  value,
  set,
  everySelect,
}: {
  value: RoutineCondition;
  set: (patch: Partial<RoutineCondition>) => void;
  everySelect: ReactNode;
}) {
  return (
    <div className="rp-tool-box">
      <div className="rw-grid">
        <Field label="Percorso" hint="Relativo all’indirizzo della connessione, es. forecast.">
          <TextField value={value.path ?? ''} onChange={(path) => set({ path })} mono />
        </Field>
        {everySelect}
      </div>
      <Field label="Parametri" hint="Uno per riga, nome=valore, es. latitude=45.46">
        <TextArea
          value={paramsText(value.params)}
          onChange={(t) => set({ params: parseParams(t) })}
          rows={3}
        />
      </Field>
      <div className="rw-grid">
        <Field label="Campo del JSON" hint="Con i punti, es. current.temperature_2m">
          <TextField value={value.field ?? ''} onChange={(field) => set({ field })} mono />
        </Field>
        <Field label="Confronto">
          <Select
            label="Confronto"
            value={value.op}
            onChange={(op) => set({ op: op as RoutineCondition['op'] })}
            options={OPS}
            width="100%"
          />
        </Field>
        <Field label="Valore" hint="Un numero, o un testo per = ≠ e «contiene».">
          <TextField value={value.target ?? ''} onChange={(target) => set({ target })} />
        </Field>
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        <span className="t-strong">Quando parte</span>
        <div role="radiogroup" aria-label="Quando parte" className="rw-kinds">
          <Choice
            on={value.fire !== 'always'}
            title="Quando diventa vera"
            hint="Una sessione sola finché resta vera; riparte quando torna falsa e poi di nuovo vera."
            onPick={() => set({ fire: 'rising' })}
          />
          <Choice
            on={value.fire === 'always'}
            title="A ogni controllo mentre è vera"
            hint="Una sessione a ogni controllo in cui la condizione regge."
            onPick={() => set({ fire: 'always' })}
          />
        </div>
      </div>
    </div>
  );
}
